import { Component } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

import { default as ReactCopyToClipboard } from 'react-copy-to-clipboard';
import { Tooltip } from '../Tooltip/Tooltip';
import { Icon } from '../Icon/Icon';
import { FormItemLabel } from '../Form/FormItemLabel';
import { Gap } from '../Gap/Gap';

const CopyToClipboardElement = styled.button`
  ${props => props.theme.partials.newForm.item.default};
  color: ${props => props.theme.vars.newColor.darkGS1};
  margin: 0;
  justify-content: space-between;
  cursor: pointer;
  text-decoration: none;
  text-align: left;
  &:after {
    ${props => props.theme.partials.newForm.item.state.default};
    content: '';
    left: 0;
    right: 0;
  }

  &:hover::after,
  &:active::after {
    ${props => props.theme.partials.newForm.item.state.active};
  }
  &:focus::after {
    ${props => props.theme.partials.newForm.item.state.focus};
  }
  &.copied-complete::after {
    ${props => props.theme.partials.newForm.item.state.complete};
    box-shadow: 0 0 0 0 transparent !important;
  }
  .value {
    margin-right: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const IconWrapperElement = styled.div`
  position: relative;
  width: 14px;
  height: 14px;
  line-height: 1;

  i {
    transition: ${props => props.theme.vars.transition.default};
    opacity: 1;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    &.hidden {
      opacity: 0;
    }
  }
`;

export interface IProps {
  /** The value that will be copied to the clipboard */
  value: string;
  /** Event fired when clicked */
  onClick?: () => void;
  /** Tab index odering */
  tabIndex?: number;
  /** CopyToClipboard label */
  label?: string;
  /** Will be passed through to the labels for attribute */
  inputId?: string;
  /** A hint can be passed as HTML to explain what the input is used for */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** optional tooltip description for the icon */
  iconTooltipDescription?: string;
  /** Optional flag to show icon only */
  showIconOnly?: boolean;
  /** Optional disabled flag */
  disabled?: boolean;
  //** Optional top gap */
  topGap?: number;
}

export interface IState {
  justCopied: boolean;
}

export class CopyToClipboard extends Component<IProps> {
  public state: IState = {
    justCopied: false,
  };
  private timeout: any;
  public getIconWrapper() {
    return (
      <IconWrapperElement>
        {this.props.showIconOnly && this.props.topGap && <Gap top={this.props.topGap} />}
        <i className={this.state.justCopied ? null : 'hidden'}>
          <Icon icon="check" color={theme.vars.newColor.green1} />
        </i>
        <i className={this.state.justCopied ? 'hidden' : null}>
          <Icon icon="copy" color={this.props.disabled ? theme.vars.color.grey4 : theme.vars.newColor.blue1} />
        </i>
      </IconWrapperElement>
    );
  }
  public render() {
    return (
      <>
        {this.props.label && (
          <FormItemLabel
            name={this.props.label}
            hint={this.props.hint}
            tooltipHeading={this.props.tooltipHeading}
            tooltipDescription={this.props.tooltipDescription}
            tooltipWidth={this.props.tooltipWidth}
            for={this.props.inputId}
          />
        )}

        <ReactCopyToClipboard
          text={this.props.value}
          onCopy={() => {
            if (this.props.disabled) {
              return false;
            }

            if (this.props.onClick) {
              this.props.onClick();
            }
            this.updateCopyState();
          }}
        >
          {this.props.showIconOnly ? (
            this.getIconWrapper()
          ) : (
            <CopyToClipboardElement
              data-testid="CopyToClipboard"
              className={this.state.justCopied ? 'copied-complete' : null}
              tabIndex={this.props.tabIndex}
              type="button"
              id={this.props.inputId}
            >
              <span className="value">{this.props.value}</span>

              {this.props.iconTooltipDescription ? (
                <Tooltip title={this.props.iconTooltipDescription}>{this.getIconWrapper()}</Tooltip>
              ) : (
                this.getIconWrapper()
              )}
            </CopyToClipboardElement>
          )}
        </ReactCopyToClipboard>
      </>
    );
  }

  public componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  private updateCopyState = () => {
    clearTimeout(this.timeout);
    this.setState({ justCopied: true });
    this.timeout = window.setTimeout(() => {
      this.setState({ justCopied: false });
    }, 3000);
  };
}
